import React, { useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixform } from "../../../../api";
import { initialValues, validationSchema } from "./EnvironmentalRiskMatrixForm.form";
import "./EnvironmentalRiskMatrixForm.scss";

const environmentalRiskMatrixCtrl = new EnvironmentalRiskMatrixform();

// Sugerencias de referencia (ISO 14001 cl. 6.1.2/6.1.3) — editables por la
// organización vía "allowAdditions", no es una lista cerrada de la norma.
const ASPECT_SUGGESTIONS = [
  "Emisiones al aire — combustión (GEI/CO₂)",
  "Emisiones al aire — COV",
  "Emisiones al aire — material particulado / polvo",
  "Emisiones al aire — refrigerantes (HFC)",
  "Emisiones al aire — olores",
  "Descarga de efluentes líquidos",
  "Agua de enfriamiento / vertido térmico",
  "Escorrentía pluvial contaminada",
  "Consumo de agua",
  "Consumo de energía",
  "Consumo de materias primas / recursos",
  "Generación de residuos sólidos",
  "Generación de residuos peligrosos",
  "Residuos reciclables / RAEE",
  "Contaminación del suelo (derrames/fugas)",
  "Ruido y vibración",
  "Impacto en biodiversidad / uso de suelo",
  "Uso y almacenamiento de químicos",
  "Empaque y diseño de producto",
  "Transporte y logística",
];

const OBLIGATION_SUGGESTIONS = [
  "Permiso de vuelco de efluentes",
  "Límite de DQO / DBO / color",
  "Permiso de captación de agua",
  "Límite de emisión atmosférica",
  "Manifiesto de residuos peligrosos",
  "Hojas de seguridad (FDS) / contención",
  "Reporte obligatorio de GEI",
  "Requisito de cliente / marca (RSL/MRSL)",
  "Sin obligación específica",
];

function toOptions(list) {
  return list.map((v) => ({ key: v, value: v, text: v }));
}

// Preview en vivo (puramente informativo) de la misma regla que aplica el
// servidor: ver applyAutoFields en server/controllers/environmentalRiskMatrix.js.
function computePreview(values) {
  const probability = Number(values.probability) || 0;
  const severity = Number(values.severity) || 0;
  const level = probability * severity;
  const significance =
    values.compliance === "Incumple" || values.compliance === "En riesgo"
      ? "Significativo"
      : "No significativo";
  return { level, significance };
}

export function EnvironmentalRiskMatrixForm(props) {
  const { onClose, onReload, item, siteSelected } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const submittingRef = useRef(false);

  const typeOptions = [
    { key: "Riesgo", value: "Riesgo", text: t("risk_matrix_type_risk") },
    { key: "Oportunidad", value: "Oportunidad", text: t("risk_matrix_type_opportunity") },
    { key: "Riesgo y oportunidad", value: "Riesgo y oportunidad", text: t("risk_matrix_type_both") },
  ];
  const conditionOptions = [
    { key: "Normal", value: "Normal", text: t("risk_matrix_condition_normal") },
    { key: "Anormal", value: "Anormal", text: t("risk_matrix_condition_abnormal") },
    { key: "Emergencia", value: "Emergencia", text: t("risk_matrix_condition_emergency") },
  ];
  const complianceOptions = [
    { key: "Cumple", value: "Cumple", text: t("risk_matrix_compliance_ok") },
    { key: "En riesgo", value: "En riesgo", text: t("risk_matrix_compliance_at_risk") },
    { key: "Incumple", value: "Incumple", text: t("risk_matrix_compliance_breach") },
  ];
  const scaleOptions = [1, 2, 3, 4, 5].map((n) => ({ key: n, value: n, text: String(n) }));

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        if (!item) {
          const payload = {
            ...formValue,
            site: user?.site || siteSelected,
          };
          const res = await environmentalRiskMatrixCtrl.createEnvironmentalRiskMatrix(accessToken, payload);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        } else {
          const res = await environmentalRiskMatrixCtrl.updateEnvironmentalRiskMatrix(accessToken, item._id, formValue);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload?.();
          onClose?.();
        }, 800);
      } catch (error) {
        toast.error(error?.msg || error?.message || t("server_connection_error"));
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const preview = computePreview(formik.values);

  return (
    <Form className="risk-matrix-form" onSubmit={formik.handleSubmit}>
      <h3 className="risk-matrix-form__title">{item ? t("risk_matrix_edit_item") : t("risk_matrix_new_item")}</h3>
      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.title && !!formik.errors.title}>
          <label>{t("title")}</label>
          <Form.Input
            name="title"
            placeholder={t("risk_matrix_title_placeholder")}
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.title && formik.errors.title && (
            <Label basic color="red" pointing>{formik.errors.title}</Label>
          )}
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_type")}</label>
          <Form.Select
            options={typeOptions}
            value={formik.values.type}
            onChange={(_, { value }) => formik.setFieldValue("type", value)}
          />
        </Form.Field>
      </div>

      <Form.Field>
        <label>{t("description")}</label>
        <Form.TextArea
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
        />
      </Form.Field>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.aspect && !!formik.errors.aspect}>
          <label>{t("risk_matrix_aspect")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            options={toOptions(ASPECT_SUGGESTIONS)}
            value={formik.values.aspect}
            placeholder={t("risk_matrix_aspect_placeholder")}
            onAddItem={(_, { value }) => ASPECT_SUGGESTIONS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("aspect", value)}
            onBlur={() => formik.setFieldTouched("aspect", true)}
          />
          {formik.touched.aspect && formik.errors.aspect && (
            <Label basic color="red" pointing>{formik.errors.aspect}</Label>
          )}
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_condition")}</label>
          <Form.Select
            options={conditionOptions}
            value={formik.values.condition}
            onChange={(_, { value }) => formik.setFieldValue("condition", value)}
          />
        </Form.Field>
      </div>

      <div className="risk-matrix-form__row">
        <Form.Field>
          <label>{t("risk_matrix_obligation")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            clearable
            options={toOptions(OBLIGATION_SUGGESTIONS)}
            value={formik.values.obligation}
            placeholder={t("risk_matrix_obligation_placeholder")}
            onAddItem={(_, { value }) => OBLIGATION_SUGGESTIONS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("obligation", value)}
          />
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_compliance")}</label>
          <Form.Select
            options={complianceOptions}
            value={formik.values.compliance}
            onChange={(_, { value }) => formik.setFieldValue("compliance", value)}
          />
        </Form.Field>
      </div>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.probability && !!formik.errors.probability}>
          <label>{t("risk_matrix_probability")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.probability}
            onChange={(_, { value }) => formik.setFieldValue("probability", value)}
            onBlur={() => formik.setFieldTouched("probability", true)}
          />
          {formik.touched.probability && formik.errors.probability && (
            <Label basic color="red" pointing>{formik.errors.probability}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.severity && !!formik.errors.severity}>
          <label>{t("risk_matrix_severity")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.severity}
            onChange={(_, { value }) => formik.setFieldValue("severity", value)}
            onBlur={() => formik.setFieldTouched("severity", true)}
          />
          {formik.touched.severity && formik.errors.severity && (
            <Label basic color="red" pointing>{formik.errors.severity}</Label>
          )}
        </Form.Field>
      </div>

      {(formik.values.probability || formik.values.severity) && (
        <div className="risk-matrix-form__preview">
          <span>
            {t("risk_matrix_level")}: <b>{preview.level}</b>
          </span>
          <span className={`risk-matrix-form__preview-sig${preview.significance === "Significativo" ? " is-significant" : ""}`}>
            {preview.significance === "Significativo"
              ? t("risk_matrix_significance_yes")
              : t("risk_matrix_significance_no")}
          </span>
        </div>
      )}

      <Form.Field>
        <label>{t("risk_matrix_controls")}</label>
        <Form.TextArea
          name="controls"
          value={formik.values.controls}
          onChange={formik.handleChange}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_action")}</label>
        <Form.TextArea
          name="action"
          value={formik.values.action}
          onChange={formik.handleChange}
        />
      </Form.Field>

      <div className="risk-matrix-form__actions">
        <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {!item ? t("save") : t("update")}
        </Button>
        <Button type="button" onClick={() => onClose?.()}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
